import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { lpForm } from 'lp-form'
import { modifyProps } from 'lp-hoc'
import {
  ScrollField as Field,
  HiddenLegend,
  CheckboxWithText,
  CheckboxFieldset,
  CheckboxGroup,
  RichTextInput,
  CloudinaryMultiFileInput,
} from 'components'
import { propTypes as formPropTypes } from 'redux-form'
import {
  ButtonArea,
  SubmitButton,
  Input,
  InputLabel,
  Select,
} from 'lp-components'
import * as flashActions from 'redux-flash'
import danceImage from 'images/discipline-icons/dance.svg'
import literaryImage from 'images/discipline-icons/literary.svg'
import musicImage from 'images/discipline-icons/music.svg'
import theaterImage from 'images/discipline-icons/theater.svg'
import visualArtsImage from 'images/discipline-icons/visual-arts.svg'
import mediaArtsImage from 'images/discipline-icons/media-arts.svg'
import otherImage from 'images/discipline-icons/other.svg'
import { scroller } from 'react-scroll'
import {
  formatObjectsToIds,
  parseIdsToObjects,
  replaceResources,
  formatObjectToId,
  parseIdToObject,
  yankBy,
  isOtherType,
  combineOtherOption,
  serializeOptions,
  displaySubmitFailure,
  useCommunity,
} from 'utils'
import * as Types from 'types'
import { startCase, once, noop, first, keys, isEmpty } from 'lodash'
import { CLOUD_RESIZE_PRESET } from 'config'

const propTypes = {
  ...formPropTypes,
  disciplines: PropTypes.arrayOf(Types.discipline).isRequired,
  onDelete: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  approachTypes: PropTypes.arrayOf(Types.communityEnumerable).isRequired,
  isNewProgram: PropTypes.bool.isRequired,
  isApplication: PropTypes.bool,
  languages: PropTypes.arrayOf(Types.language).isRequired,
  artistId: PropTypes.number,
  flashSuccessMessage: PropTypes.func.isRequired,
  flashErrorMessage: PropTypes.func.isRequired,
}

const defaultProps = {
  onSubmitSuccess: noop,
  isApplication: false,
}

const ProgramImagesLabel = ({ ...props }) => (
  <InputLabel {...props}>
    Upload photos associated with this program (e.g., activities, events, or
    materials connected to it).
    <span className="input-instructions">
      (Accepted formats include png, jpg and jpeg. Your file must be less than
      10 MB.)
    </span>
  </InputLabel>
)

const DISCIPLINE_IMAGE_MAP = {
  dance: danceImage,
  literary_arts: literaryImage,
  music: musicImage,
  theatre: theaterImage,
  visual_arts: visualArtsImage,
  media_arts: mediaArtsImage,
  other: otherImage,
}

function gradeForId(grades, id) {
  return grades.find((grade) => grade.id == id)
}

const validateGradeOrder = once((grades) => {
  return function validate(value, { minGrade, maxGrade }) {
    if (!minGrade || !maxGrade) return ''
    const [fullMinGrade, fullMaxGrade] = [minGrade, maxGrade].map(({ id }) =>
      gradeForId(grades, id)
    )
    if (!fullMinGrade || !fullMaxGrade) return ''
    if (fullMinGrade.position >= fullMaxGrade.position)
      return 'Grades must be in correct order.'
  }
})

function ProgramForm({
  handleSubmit,
  pristine,
  submitting,
  onDelete,
  disciplines,
  outcomeTypes,
  disabled,
  grades,
  programTypes,
  approachTypes,
  onSubmitSuccess,
  onSubmitFail,
  form,
  isNewProgram,
  isApplication,
  languages,
  artistId,
  flashSuccessMessage,
  flashErrorMessage,
}) {
  const uniqueId = (inputName) => `${form}-${inputName}`
  const community = useCommunity()
  const photoEnabled = community?.teachingArtist?.programPhotoEnabled
  const applicationUploadFolder = useMemo(() => Date.now(), [])
  const programImagesUploadPath = isApplication
    ? `/artists/programs/images/initialValuesId/${applicationUploadFolder}/`
    : `/artists/programs/images/${artistId}`

  const [otherOutcomeType, definedOutcomeTypes] = useMemo(
    () => yankBy(outcomeTypes, isOtherType),
    [outcomeTypes]
  )
  const [otherApproachType, definedApproachTypes] = useMemo(
    () => yankBy(approachTypes, isOtherType),
    [approachTypes]
  )
  const [otherLanguage, definedLanguages] = useMemo(
    () => yankBy(languages, isOtherType),
    [languages]
  )
  return (
    <form onSubmit={handleSubmit}>
      {!disabled && (
        <ButtonArea>
          <SubmitButton {...{ pristine, submitting, style: 'secondary' }}>
            Save Program
          </SubmitButton>
          <button
            type="button"
            onClick={() => {
              if (!isNewProgram)
                return onDelete().then(onSubmitSuccess).catch(onSubmitFail)
              return onDelete()
            }}
            className="link-warn"
          >
            <span aria-hidden>×</span> Delete Program
          </button>
        </ButtonArea>
      )}
      <div className="row">
        <div className="col-6">
          <Field
            name="name"
            id={uniqueId('name')}
            component={Input}
            disabled={disabled}
          />
        </div>
        <div className="col-6">
          <div className="grade-block">
            <Field
              name="minGrade"
              id={uniqueId('minGrade')}
              placeholder="Choose"
              component={Select}
              options={serializeOptions(grades)}
              format={formatObjectToId('id')}
              parse={parseIdToObject('id')}
              label="Grades"
              aria-label="Start grade"
              disabled={disabled}
              validate={validateGradeOrder(grades)}
            />
            <p>through</p>
            <Field
              name="maxGrade"
              id={uniqueId('maxGrade')}
              placeholder="Choose"
              component={Select}
              validate={validateGradeOrder(grades)}
              options={serializeOptions(grades)}
              format={formatObjectToId('id')}
              parse={parseIdToObject('id')}
              label={false}
              aria-label="End grade"
              disabled={disabled}
            />
          </div>
        </div>
      </div>
      <Field
        name="description"
        id={uniqueId('description')}
        component={RichTextInput}
        className="rich-text"
        disabled={disabled}
      />
      {photoEnabled && (isApplication || artistId != null) && (
        <Field
          name="programImages"
          component={CloudinaryMultiFileInput}
          labelComponent={ProgramImagesLabel}
          uploadPath={programImagesUploadPath}
          uploadPreset={CLOUD_RESIZE_PRESET}
          accept=".png,.jpg,.jpeg"
          onUploadSuccess={() =>
            flashSuccessMessage(
              'Program photo uploaded. Make sure to save your changes.'
            )
          }
          onUploadFailure={flashErrorMessage}
          captionLabel={(filename) => `Add Image Caption for ${filename}`}
          disabled={disabled}
        />
      )}
      {!isEmpty(approachTypes) && (
        <CheckboxFieldset>
          <legend>
            <strong>
              Which of the following describes how your program approaches arts
              instruction to accomplish its objectives?
            </strong>
          </legend>
          <div className="response-option-blocks full-width">
            <div className="response-option-block">
              <Field
                name="approaches"
                id={uniqueId('approaches')}
                labelComponent={HiddenLegend}
                component={CheckboxGroup}
                format={formatObjectsToIds('approachId')}
                parse={parseIdsToObjects('approachId')}
                options={serializeOptions(definedApproachTypes)}
                disabled={disabled}
              />
              {otherApproachType && (
                <Field
                  name="otherApproach.otherText"
                  id={uniqueId('otherApproach.otherText')}
                  label={otherApproachType.displayName}
                  placeholder="Description..."
                  component={CheckboxWithText}
                  className="CheckboxGroup full-width-other"
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </CheckboxFieldset>
      )}
      {!isEmpty(languages) && (
        <CheckboxFieldset>
          <legend>
            <strong>What languages do you provide this program in</strong>
          </legend>
          <div className="response-option-blocks full-width">
            <div className="response-option-block">
              <Field
                name="languages"
                id={uniqueId('languages')}
                labelComponent={HiddenLegend}
                component={CheckboxGroup}
                format={formatObjectsToIds('languageId')}
                parse={parseIdsToObjects('languageId')}
                options={serializeOptions(definedLanguages)}
                disabled={disabled}
              />
              {otherLanguage && (
                <Field
                  name="otherLanguage.otherText"
                  id={uniqueId('otherLanguage.otherText')}
                  label={otherLanguage.displayName}
                  placeholder="Description..."
                  component={CheckboxWithText}
                  className="CheckboxGroup full-width-other"
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </CheckboxFieldset>
      )}
      <fieldset>
        <legend>
          <strong>Program Types</strong>
        </legend>
        <div className="response-option-blocks full-width">
          <div className="response-option-block">
            <Field
              name="programTypes"
              id={uniqueId('programTypes')}
              label={false}
              component={CheckboxGroup}
              format={formatObjectsToIds('programTypeId')}
              parse={parseIdsToObjects('programTypeId')}
              options={serializeOptions(programTypes)}
              disabled={disabled}
            />
          </div>
        </div>
      </fieldset>
      {!isEmpty(outcomeTypes) && (
        <CheckboxFieldset>
          <legend>
            <strong>
              The list below includes qualities and intended outcomes that are
              sometimes the central focus of an arts education program. Please
              select any that are essential elements of this program.
            </strong>
          </legend>
          <div className="response-option-blocks full-width">
            <div className="response-option-block">
              <Field
                name="outcomes"
                id={uniqueId('outcomes')}
                labelComponent={HiddenLegend}
                component={CheckboxGroup}
                format={formatObjectsToIds('outcomeTypeId')}
                parse={parseIdsToObjects('outcomeTypeId')}
                options={serializeOptions(definedOutcomeTypes)}
                disabled={disabled}
              />
              {otherOutcomeType && (
                <Field
                  name="otherOutcome.otherText"
                  id={uniqueId('otherOutcome.otherText')}
                  label={otherOutcomeType.displayName}
                  placeholder="Description..."
                  component={CheckboxWithText}
                  className="CheckboxGroup full-width-other"
                  disabled={disabled}
                />
              )}
            </div>
          </div>
        </CheckboxFieldset>
      )}
      <fieldset>
        <legend>
          <strong>Arts discipline(s) addressed by this program:</strong>
        </legend>
        <div className="response-option-blocks">
          {disciplines.map(({ name, subDisciplines }, i) => (
            <div key={i} className="response-option-block">
              <p>
                <img
                  src={DISCIPLINE_IMAGE_MAP[name]}
                  alt=""
                  className="discipline-icon"
                />
                <strong>{startCase(name)}</strong>
              </p>
              <Field
                name="subDisciplines"
                id={uniqueId('subDisciplines')}
                label={false}
                component={CheckboxGroup}
                format={formatObjectsToIds('subDisciplineId')}
                parse={parseIdsToObjects('subDisciplineId')}
                options={serializeOptions(subDisciplines)}
                disabled={disabled}
              />
            </div>
          ))}
        </div>
      </fieldset>
    </form>
  )
}

ProgramForm.propTypes = propTypes
ProgramForm.defaultProps = defaultProps

function modifyBeforeSubmit({ initialValues }) {
  return {
    beforeSubmit: ({
      programTypes,
      outcomes,
      otherOutcome,
      subDisciplines,
      maxGrade,
      minGrade,
      approaches,
      otherApproach,
      languages,
      otherLanguage,
      ...rest
    }) => {
      const outcomesResources = replaceResources({
        old: combineOtherOption(
          initialValues.outcomes,
          initialValues.otherOutcome
        ),
        new: combineOtherOption(outcomes, otherOutcome),
      })
      const programTypesResources = replaceResources({
        old: initialValues.programTypes,
        new: programTypes,
      })
      const subDisciplinesResources = replaceResources({
        old: initialValues.subDisciplines,
        new: subDisciplines,
      })
      const approachesResources = replaceResources({
        old: combineOtherOption(
          initialValues.approaches,
          initialValues.otherApproach
        ),
        new: combineOtherOption(approaches, otherApproach),
      })
      const languagesResources = replaceResources({
        old: combineOtherOption(
          initialValues.languages,
          initialValues.otherLanguage
        ),
        new: combineOtherOption(languages, otherLanguage),
      })
      return {
        ...rest,
        programTypes: programTypesResources,
        outcomes: outcomesResources,
        subDisciplines: subDisciplinesResources,
        maxGradeId: formatObjectToId('id', maxGrade),
        minGradeId: formatObjectToId('id', minGrade),
        approaches: approachesResources,
        languages: languagesResources,
      }
    },
  }
}

export default compose(
  connect(null, {
    flashSuccessMessage: flashActions.flashSuccessMessage,
    flashErrorMessage: flashActions.flashErrorMessage,
  }),
  modifyProps(modifyBeforeSubmit),
  lpForm({
    enableReinitialize: true,
    constraints: {
      name: { presence: true },
      description: { presence: true },
      maxGrade: { presence: { message: '^Grade is required' } },
      minGrade: { presence: { message: '^Grade is required' } },
      'otherOutcome.otherText': {
        exclusion: {
          within: [' '],
          message: '^Description is required',
        },
      },
      'otherApproach.otherText': {
        exclusion: {
          within: [' '],
          message: '^Description is required',
        },
      },
      'otherLanguage.otherText': {
        exclusion: {
          within: [' '],
          message: '^Description is required',
        },
      },
    },
    onSubmitFail: (params, dispatch, error, { syncErrors }) => {
      displaySubmitFailure(params, dispatch, error)
      const firstErrorField = first(keys(syncErrors))
      const { otherText } = syncErrors[firstErrorField]
      const firstErrorFieldName = otherText
        ? firstErrorField + '.otherText'
        : firstErrorField
      return scroller.scrollTo(firstErrorFieldName, { smooth: true })
    },
  })
)(ProgramForm)
